import IRIentry from '../../core/IRIentry';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import List from '@material-ui/core/List';
import ListSubheader from '@material-ui/core/ListSubheader';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';
import AddButton from '../../core/AddButton';
import CancelButton from '../../core/CancelButton';
import Help from '../../core/Help';
import LabelledSwitch from '../../core/LabelledSwitch';
import LanguageSelector from '../../core/LanguageSelector';
import ListResult from '../../core/ListResult';
import PrimaryButton from '../../core/PrimaryButton';
import { globalStyles as styles } from '../../core/style/gstheme';
import TitledPanel from '../../core/TitledPanel';
import TitledTextField from '../../core/TitledTextField';
import { debounce, makeCancelable, sanitiseText, sortByLength } from '../../core/utils/utils';
import GS from '../../grafsync/grafsync';
import SchemeLink from '../core/SchemeLink';

class LanguageField extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            open: false,
            acOpen: false,
            viewIRI: false,
            createAsXL: props.useXL,
            checking: false,
            checked: false,
            isUnusedLabel: null,
            isDuplicate: false,
            isDeleted: false,
            useUnusedLabel: false,
            labelResourceIRI: "",
            labelComputedIRI: "",
            uniqueLabelIRI: false,
            text: "",
            lang: null
        };
    }

    handleClickOpen = () => {
        const langsToUse = this.computeLangsToUse();
        const selectedLang = this.props.viewLang || this.props.defaultLang;
        // If only one language option select it
        const initialLang = langsToUse.includes(selectedLang) ? selectedLang : (
            langsToUse.length === 1 ? langsToUse[0] : null
        );
        this.setState({
            open: true,
            lang: initialLang
        });
    };

    findUnusedXLlabel = (value, lang) => {
        if (!this.props.schemeIRI) throw new Error("Cannot use LanguageField without scheme IRI for XL label reuse.");
        let searchParts = [
            {
                predicate: "http://www.w3.org/2004/02/skos/core#inScheme",
                value: this.props.schemeIRI
            },
            {
                predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
                value: "http://www.w3.org/2008/05/skos-xl#Label"
            },
            {
                predicate: "http://www.w3.org/2008/05/skos-xl#literalForm",
                value: value,
                isText: true,
                isExactMatch: false
            }
        ];
        this.doAutocomplete(
            {
                graph: [this.props.graph],
                search: searchParts,
                excludeByPath: [
                    {
                        predicate: "https://graphifi.com/schema/xkos/isXlPrefLabelOf"
                    },
                    {
                        predicate: "https://graphifi.com/schema/xkos/isXlAltLabelOf"
                    },
                    {
                        predicate: "https://graphifi.com/schema/xkos/isXlHiddenLabelOf"
                    }
                ],
                language: lang,
                pageSize: 1,
                predicates: ["http://www.w3.org/2008/05/skos-xl#literalForm"]
            }
        )
            .promise
            .then((data) => {
                const isExisting = data.results && data.results.length > 0 ?
                    data.results.find(result => !result.invalid && result.literalForm[lang].toLowerCase() === value.toLowerCase())
                    :
                    null;
                if (isExisting) {
                    this.setState({
                        isUnusedLabel: isExisting.id,
                        unusedLabelForm: isExisting.literalForm[lang],
                        useUnusedLabel: true,
                        checking: false
                    });
                }
                else {
                    this.setState({
                        isUnusedLabel: null,
                        useUnusedLabel: false,
                        checking: false
                    });
                }
            })
            .catch((e) => {
                if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
            });
    };

    findConcept = (value, lang) => {
        let searchParts = [
            {
                predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
                value: "http://www.w3.org/2004/02/skos/core#Concept"
            },
            {
                predicate: "http://www.w3.org/2004/02/skos/core#prefLabel",
                value: value,
                isExactMatch: false
            }
        ];
        this.doAutocomplete(
            {
                graph: [this.props.graph],
                search: searchParts,
                language: lang,
                entailment: this.props.optionForXL ? ["propertyChain"] : [],
                predicates: [
                    "http://www.w3.org/2004/02/skos/core#prefLabel",
                    "http://www.w3.org/2004/02/skos/core#inScheme",
                    "https://grafsync.graphifi.com/schema/gs/iri",
                    "https://grafsync.graphifi.com/schema/gs/pendingDeletion"
                ]
            })
            .promise
            .then((data) => {
                const sortedResults = data.results && data.results.length > 0 ? sortByLength([...data.results], 'prefLabel', lang).slice(0, 5) : null;
                this.setState({
                    results: sortedResults,
                    acOpen: data.results && data.results.length > 0 ? true : false
                });
                // It's a duplicate if there is an equivalent label in the in-context scheme
                const isDuplicate = data.results && data.results.length > 0 ? data.results.some(result => {
                    // We may get an edge case of mutiple prefLabels in a single language
                    if (Array.isArray(result.prefLabel[lang])) {
                        return result.prefLabel[lang].some(label =>
                            label.toLowerCase() === this.state.label.toLowerCase()
                            && result.inScheme && result.inScheme.includes(this.props.schemeIRI));
                    }
                    return result.prefLabel[lang].toLowerCase() === this.state.text.toLowerCase()
                        && result.inScheme && result.inScheme.includes(this.props.schemeIRI);
                }) : false;
                this.setState({ isDeleted: false, isDuplicate: isDuplicate, isUnusedLabel: null, checked: true });
                if (!this.props.optionForXL) {
                    this.setState({ checking: false });
                }
                else {
                    if (!isDuplicate || !this.props.checkForDuplicates) {
                        this.findUnusedXLlabel(value, lang);
                    }
                    else this.setState({ checking: false });
                }
            })
            .catch((e) => {
                if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
            });
    };

    doAutocomplete(payload) {
        this.cancellable = makeCancelable(GS.autocomplete(payload));
        return this.cancellable;
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
        if (this.hcTimeout) clearTimeout(this.hcTimeout);
    }

    reserveIDs = async () => {
        this.setState({ processingIRI: true });
        // Make IRI reservation call
        const isFound = await GS.reserveIRI(this.state.labelResourceIRI, this.props.graph, "http://www.w3.org/2008/05/skos-xl#Label");
        const exists = isFound.exists;
        if (exists) {
            this.rejectIRI(true);
            return false;
        }
        const reservationID = isFound.id;
        return {
            reservationID
        };
    };

    loadIDs() {
        this.cancellable = makeCancelable(this.reserveIDs());
        return this.cancellable;
    }

    addLabel = async () => {
        if (this.state.createAsXL) {
            const ids = await this.loadIDs().promise;
            if (this.cancellable && this.cancellable.isCancelled()) return true;
            if (ids !== false) {
                this.setState({ open: false, acOpen: false }, () => {
                    setTimeout(() => this.processLabel(ids), 10);
                });
            }
        }
        else {
            this.processLabel();
        }
    };

    processLabel = (ids) => {
        // We need to make sure we have the parent loaded so narrower events happens
        let reservationID;
        if (ids && ids.reservationID) reservationID = ids.reservationID;
        const labelIRI = this.state.createAsXL ?
            this.state.labelResourceIRI
            :
            this.state.isUnusedLabel;
        this.props.onAddLanguage(this.state.lang, this.state.text, this.state.createAsXL, labelIRI, reservationID);
        // We do not reset the XL option working on the assumption if done a particular way once user wants it that way again
        this.exitAddLabel();
    };

    exitAddLabel = () => {
        const langsToUse = this.computeLangsToUse();
        this.setState({
            open: false,
            acOpen: false,
            text: "",
            isUnusedLabel: null,
            isDuplicate: false,
            isDeleted: false,
            processingIRI: false,
            useUnusedLabel: false,
            uniqueLabelIRI: false,
            iriRejected: false,
            checking: false,
            checked: true,
            viewIRI: false,
            labelComputedIRI: "",
            labelResourceIRI: "",
            lang: this.props.defaultLang && langsToUse.includes(this.props.defaultLang) ? this.props.defaultLang : null
        });
    };

    getIRI = () => {
        let labelComputedIRI = "";
        const iriOptions = {
            prependText: this.props.iriOptions.xlOptions.prependText,
            appendText: this.props.iriOptions.xlOptions.shouldAppendText ? this.state.lang : "",
            textSeparator: "-"
        };
        labelComputedIRI = GS.computeIRI(this.props.schemeIRI, this.state.text, this.props.useSlashIRI, this.props.useUuidIRI, iriOptions);
        return labelComputedIRI;
    };

    selectLanguage = (lang) => {
        this.setState({ lang }, () => {
            if (this.state.text !== "") {
                const labelComputedIRI = this.getIRI();
                this.setState({ labelComputedIRI, checking: true }, debounce(this.findConcept, this, [this.state.text, this.state.lang]));
            }
        });
    };

    handleChange = event => {
        const t = event.target;
        const cleanText = sanitiseText(t.value.trimLeft());
        this.setState({ [t.name]: cleanText, checked: false }, () => {
            const invalid = this.props.existingValues
                && this.props.existingValues[this.state.lang]
                && this.props.existingValues[this.state.lang]
                    .map(value => value.toLowerCase())
                    .includes(t.value.trimLeft().toLowerCase());
            if (!invalid && this.props.checkForLabel && this.state.lang && cleanText !== "") {
                if (!this.props.useUuidIRI || this.state.labelComputedIRI === "") {
                    const labelComputedIRI = this.getIRI();
                    this.setState({ labelComputedIRI });
                }
                this.setState({ checking: true }, debounce(this.findConcept, this, [cleanText, this.state.lang]));
            }
            else {
                this.setState({ checking: false, checked: true, acOpen: false, labelComputedIRI: "" });
            }
        });
    };

    handleXLChange = checked => {
        if (checked) {
            const labelComputedIRI = this.getIRI();
            this.setState({ labelComputedIRI });
        }
        this.setState({
            createAsXL: checked,
            ...(checked) && {
                useUnusedLabel: false
            }
        });
    };

    handleUnusedLabelChange = checked => {
        this.setState({
            useUnusedLabel: checked,
            ...(checked) && {
                createAsXL: false
            }
        });
    };

    computeLangsToUse() {
        const { onePerLang, existingLangs, langs } = this.props;
        return onePerLang && existingLangs ? langs.filter(lang => !existingLangs.includes(lang)) : langs;
    }

    resetLabelIRIconfirmation = () => this.setState({ uniqueLabelIRI: false, iriRejected: false });

    confirmLabelUniqueIRI = (iri) =>
        this.setState({ uniqueLabelIRI: true, labelResourceIRI: iri, iriRejected: false });

    rejectIRI = () => this.setState({ uniqueLabelIRI: false, iriRejected: true, processingIRI: false });

    handleClickAway = () => this.setState({ acOpen: false });

    handleShowChange = checked => this.setState({ viewIRI: checked });

    render() {

        const { existingValues, optionForXL, title, schemeIRI, multiline, checkForDuplicates, existingLangs, onePerLang,
            useSlashIRI, useUuidIRI, textRef, maxLength, classes, name, defaultLang, placeholder, graph, iriOptions } = this.props;
        const { text, lang, createAsXL, useUnusedLabel, isUnusedLabel, checking, labelComputedIRI, uniqueLabelIRI,
            isDuplicate, checked, isDeleted, acOpen, results, processingIRI } = this.state;
        const langsToUse = this.computeLangsToUse();
        const langFields = existingValues && existingValues[lang] && lang ?
            (Array.isArray(existingValues[lang]) ?
                existingValues[lang]
                :
                [existingValues[lang]]
            )
            : null;
        const existingOnePerLang = existingLangs && onePerLang && existingLangs.includes(lang);

        const lowerText = text.toLowerCase();
        const existing = langFields ? langFields.map(value => value.toLowerCase()).includes(lowerText.trim()) : null;
        const invalid = text !== "" && (existing || (isDuplicate && checkForDuplicates)
            || checking || !checked || isDeleted || existingOnePerLang || (createAsXL && !uniqueLabelIRI));

        const viewIRI = iriOptions.showIRI || this.state.viewIRI;

        return (
            <React.Fragment>
                <AddButton dataTest="add-language-value" onClick={this.handleClickOpen} title="Add a new language value" />
                <Dialog
                    data-test="add-language-field"
                    maxWidth="md"
                    fullWidth
                    classes={{
                        paper: classes.paper,
                        paperWidthMd: classes.largeDialog
                    }}
                    className={classes.dialog}
                    open={this.state.open}
                    onClose={this.exitAddLabel}
                    aria-labelledby="text-dialog-title">
                    <DialogTitle data-test="add-language-field-title"
                        disableTypography className={classes.mainHeading} id="text-dialog-title">{title ? title : 'Add Label'}</DialogTitle>
                    <DialogContent style={{
                        height: optionForXL ?
                            viewIRI ?
                                '480px'
                                :
                                '330px'
                            :
                            viewIRI ?
                                '360px'
                                :
                                '220px'
                    }}>
                        <Help textRef={textRef} />
                        <TitledPanel title="Language">
                            <LanguageSelector
                                includeLabel={false}
                                selectedLang={lang}
                                langs={langsToUse}
                                onSelect={this.selectLanguage}
                                defaultLang={defaultLang} />
                        </TitledPanel>
                        <form autoComplete="off" noValidate="novalidate">
                            <TitledTextField
                                dataTest="language-value"
                                name="text"
                                title={name || "Text"}
                                autoFocus={lang !== null}
                                required={true}
                                text={text}
                                placeholder={placeholder || "Text"}
                                margin={true}
                                multiline={multiline}
                                maxLength={maxLength || 500}
                                lang={lang}
                                error={(isDuplicate && checkForDuplicates) || existing || existingOnePerLang}
                                errorText={existing ? "This value already exists on this concept in this language."
                                    :
                                    (
                                        isDuplicate ?
                                            "This value already exists on another concept in this language."
                                            :
                                            existingOnePerLang ?
                                                "This language has been added by another user. Please Cancel."
                                                :
                                                null
                                    )
                                }
                                helperText={
                                    (isUnusedLabel || isDeleted) ?
                                        (isUnusedLabel ?
                                            "This value exists as an unused SKOS-XL label (" + this.state.unusedLabelForm + ")."
                                            :
                                            "This values exists as a deleted SKOS-XL label."
                                        )
                                        :
                                        null
                                }
                                onEnter={!isDuplicate && checked ? this.addLabel : null}
                                onChange={this.handleChange} />
                            {acOpen && results && results.length > 0 &&
                                <ClickAwayListener onClickAway={this.handleClickAway}>
                                    <List
                                        data-test="list-results"
                                        dense
                                        className={classes.existingResults}
                                        subheader={<ListSubheader className={classes.existingResultsHeader}>Existing Concepts</ListSubheader>}
                                        style={{ marginTop: -8 }}>
                                        {results.map((result, i) => {
                                            let label = Array.isArray(result.prefLabel[lang]) ?
                                                result.prefLabel[lang].sort().join(" AND ")
                                                :
                                                result.prefLabel[lang];
                                            let additionalLabel = null;
                                            if (result.inScheme && !result.inScheme.includes(schemeIRI)) {
                                                additionalLabel = <div>
                                                    {result.inScheme.map(scheme =>
                                                        <SchemeLink handleNotAvailable={true} key={scheme} id={scheme} graph={graph} defaultLang={defaultLang} />
                                                    )}
                                                </div>;
                                            }
                                            const currentIRI = result.iri ?
                                                result.iri[result.iri.length - 1]
                                                :
                                                result.id;

                                            return <ListResult
                                                key={i}
                                                text={label}
                                                iri={currentIRI}
                                                pendingDeletion={result.pendingDeletion}
                                                invalid={true}
                                                entailed={result.isEntailed}
                                                additionalLabel={additionalLabel}
                                            />;
                                        })}
                                    </List>
                                </ClickAwayListener>
                            }
                            {optionForXL &&
                                <div>
                                    <div style={{ textAlign: 'right' }}>
                                        <LabelledSwitch
                                            dataTest="createAsXL"
                                            disabled={lang === null || text.length === 0 || invalid || checking}
                                            checked={createAsXL}
                                            handleChange={this.handleXLChange}
                                            label="Create as XL Label" />
                                    </div>
                                    {createAsXL && !iriOptions.showIRI &&
                                        <div style={{ textAlign: 'right' }}>
                                            <LabelledSwitch
                                                dataTest="show-iri"
                                                checked={viewIRI}
                                                disabled={text === "" || processingIRI}
                                                handleChange={this.handleShowChange}
                                                label="View/Edit IRI" />
                                        </div>
                                    }
                                    {!isUnusedLabel && createAsXL &&
                                        <IRIentry
                                            title="Label IRI"
                                            key={labelComputedIRI}
                                            disabled={processingIRI}
                                            startIRI={labelComputedIRI}
                                            visible={viewIRI}
                                            handleIRIclash={iriOptions.handleIRIclash}
                                            useSlashIRI={useSlashIRI}
                                            graph={graph}
                                            resetIRIconfirmation={this.resetLabelIRIconfirmation}
                                            confirmUniqueIRI={this.confirmLabelUniqueIRI}
                                            rejectIRI={this.rejectIRI}
                                            useUuidIRI={useUuidIRI}
                                        />
                                    }
                                </div>
                            }
                            {optionForXL && isUnusedLabel &&
                                <div style={{ textAlign: 'right' }}>
                                    <LabelledSwitch
                                        dataTest="useUnusedXL"
                                        disabled={!isUnusedLabel}
                                        checked={useUnusedLabel}
                                        handleChange={this.handleUnusedLabelChange}
                                        label="Use Existing Unused Label" />
                                </div>
                            }
                        </form>
                    </DialogContent>
                    <DialogActions className={classes.dialogActions}>
                        <CancelButton onClick={this.exitAddLabel} />
                        <PrimaryButton
                            disabled={lang === null || text.length === 0 || invalid || checking}
                            onClick={this.addLabel}>Add</PrimaryButton>
                    </DialogActions>
                </Dialog>
            </React.Fragment>
        );
    }
}

LanguageField.defaultProps = {
    multiline: false,
    optionForXL: false,
    useXL: false,
    onePerLang: false,
    checkForLabel: true,
    checkForDuplicates: true,
    maxLength: 100,
    iriOptions: {}
};

LanguageField.propTypes = {
    maxLength: PropTypes.number,
    graph: PropTypes.string.isRequired,
    optionForXL: PropTypes.bool,
    onAddLanguage: PropTypes.func.isRequired,
    useXL: PropTypes.bool,
    multiline: PropTypes.bool,
    schemeIRI: PropTypes.string,
    existingValues: PropTypes.object,
    checkForDuplicates: PropTypes.bool,
    name: PropTypes.string,
    onePerLang: PropTypes.bool,
    placeholder: PropTypes.string,
    textRef: PropTypes.string,
    defaultLang: PropTypes.string.isRequired,
    viewLang: PropTypes.string,
    title: PropTypes.string.isRequired,
    useSlashIRI: PropTypes.bool,
    useUuidIRI: PropTypes.bool,
    iriOptions: PropTypes.object
};

export default withStyles(styles)(LanguageField);